import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import WPDropdown from "../WPDropdown";
import { WPRangedInput } from "../WPInput";
import WPInputLabel from "../WPInputLabel";
import { WPTypography } from "../WPTypography";
import { PAGINATION_LABEL_FALLBACKS } from "./Pagination.constants";
import PaginationControls from "./PaginationControls";

import { PAGE_SIZE_OPTIONS } from "../../constants/global.constants";

import "./WPPagination.scss";

/**
 * Pagination component for navigating paged data.
 * @param {Object} props
 * @param {number} props.totalItems - Total number of items.
 * @param {number[]} props.pageSizeOptions - Options for items per page.
 * @param {number} props.currentPage - Current active page.
 * @param {number} props.pageSize - Current page size.
 * @param {function} props.onPageChange - Callback(page, pageSize) when page or size changes.
 */
const WPPagination = ({
  totalItems,
  pageSizeOptions = PAGE_SIZE_OPTIONS,
  currentPage,
  pageSize,
  onPageChange,
  showStartEllipsis = true,
  showEndEllipsis = true,
}) => {
  const { t } = useTranslation();

  const paginationLabel = {
    total: t("pagination.total") || PAGINATION_LABEL_FALLBACKS.totalLabel,
    goTo: t("pagination.goTo") || PAGINATION_LABEL_FALLBACKS.goToLabel,
    pageSize:
      t("pagination.pageSize") || PAGINATION_LABEL_FALLBACKS.pageSizeLabel,
  };

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(totalItems / pageSize)),
    [totalItems, pageSize],
  );

  const [pageInputValue, setPageInputValue] = useState("");

  /**
   * Handles changing the current page.
   * Only triggers if the new page is within valid range and different from current.
   */
  const handlePageChange = useCallback(
    (page) => {
      if (page >= 1 && page <= totalPages && page !== currentPage) {
        onPageChange(page, pageSize);
      }
    },
    [onPageChange, pageSize, totalPages, currentPage],
  );

  /** Handles changing the page size. Resets to page 1 if the page size changes. */
  const handlePageSizeChange = useCallback(
    (data) => {
      const newSize = Number(data.value);
      if (pageSize !== newSize) {
        onPageChange(1, newSize);
      }
    },
    [onPageChange, pageSize],
  );

  /** Validates and navigates to the page entered in the "Go to" input field. */
  const validateAndNavigateToInputPage = useCallback(() => {
    if (pageInputValue === "") return;

    let page = Number(pageInputValue);
    if (isNaN(page)) return;

    page = Math.max(1, Math.min(page, totalPages));
    handlePageChange(page);
    setPageInputValue("");
  }, [pageInputValue, totalPages, handlePageChange]);

  const handlePageInputChange = (event) => {
    const { value } = event.target;
    setPageInputValue(value);
  };

  // Always show all options, but disable by style if not enough items
  const pageSizeDropdownOptions = pageSizeOptions.map((size) => ({
    value: size,
    label: `${size}/${paginationLabel.pageSize}`,
    disabled: totalItems <= size,
  }));

  return (
    <div className="d-flex app-max-width magrin-auto pagination">
      <div className="pagination-select">
        {showStartEllipsis && (
          <>
            <WPInputLabel
              id="total_elements"
              label={
                <>
                  <span className="total-label">{paginationLabel.total}</span>
                  <span className="total-items">{totalItems}</span>
                </>
              }
              className="total-elements"
            />
            <WPDropdown
              id="page_size_select"
              value={{
                value: pageSize,
                label: `${pageSize}/${paginationLabel.pageSize}`,
              }}
              options={pageSizeDropdownOptions}
              onChange={handlePageSizeChange}
              getOptionLabel={(option) => option.label}
              // No need to disable options here, backend can handle it and can treat it as a upper limit.
              // isOptionDisabled={(option) => option.disabled}
              // disabled={totalItems <= PAGE_SIZE_OPTIONS[0]}
            />
          </>
        )}
      </div>

      <PaginationControls
        currentPage={currentPage}
        totalPages={totalPages}
        handlePageChange={handlePageChange}
      />

      <div className="pagination-input">
        {showEndEllipsis && (
          <>
            <WPInputLabel
              id="pagination_goto_label"
              label={paginationLabel.goTo}
              className="pagination_goto_label"
            />
            <WPRangedInput
              id="pagination_goto_input"
              value={pageInputValue || ""}
              onChange={handlePageInputChange}
              onBlur={validateAndNavigateToInputPage}
              className="pagination-goto-input"
              wrapperClassName="pagination-goto-input-wrapper"
              integer
            />
            <WPTypography text={`/ ${totalPages}`} />
          </>
        )}
      </div>
    </div>
  );
};

export default WPPagination;
